import dayjs from 'dayjs';
import type { ContestPhaseDateRow } from './dateUtils';
import { sanitizePhaseRows, shiftPhasesToToday, todayStart } from './dateUtils';
import { defaultAiSelectedPhases, parseSelectedPhases, sortPhasesByCanonicalOrder } from './phaseConfig';
import type { ContestWizardState } from './types';

export function generateTimelinePhasesFromAnswers(
    answers: Record<string, string>,
    submissionState: ContestWizardState['submission_state'],
): { phases: ContestPhaseDateRow[]; contest_phases_enabled: true } {
    const selected = parseSelectedPhases(answers.selected_phases);
    const fallback = defaultAiSelectedPhases(submissionState);
    let order = selected.length ? selected : sortPhasesByCanonicalOrder([...fallback]);

    let firstPhase = answers.first_phase || order[0];
    if (!order.includes(firstPhase)) {
        firstPhase = order[0];
    }

    const duration = Math.max(1, parseInt(String(answers.phase_duration_days || '14').replace(/\D/g, ''), 10) || 14);

    const today = todayStart();
    let start = dayjs(answers.start_date).startOf('day');
    if (!start.isValid()) {
        start = today.add(7, 'day');
    }
    if (start.isBefore(today)) {
        start = today;
    }

    const phaseDates: Record<string, { start: dayjs.Dayjs; end: dayjs.Dayjs }> = {};
    const firstIdx = order.indexOf(firstPhase);

    phaseDates[firstPhase] = { start, end: start.add(duration - 1, 'day') };

    for (let i = firstIdx - 1; i >= 0; i--) {
        const phase = order[i];
        const nextPhase = order[i + 1];
        const end = phaseDates[nextPhase].start.subtract(1, 'day');
        phaseDates[phase] = { start: end.subtract(duration - 1, 'day'), end };
    }

    for (let i = firstIdx + 1; i < order.length; i++) {
        const phase = order[i];
        const prevPhase = order[i - 1];
        const phaseStart = phaseDates[prevPhase].end.add(1, 'day');
        phaseDates[phase] = { start: phaseStart, end: phaseStart.add(duration - 1, 'day') };
    }

    const phases = sanitizePhaseRows(
        shiftPhasesToToday(
            order.map(phase => ({
                phase,
                date_started: phaseDates[phase].start.format('YYYY-MM-DD'),
                expiration_date: phaseDates[phase].end.format('YYYY-MM-DD'),
            })),
        ),
    );

    return { phases, contest_phases_enabled: true };
}
